/**
 * API — Honorarios / Cobranza (Gestión Despacho, PR 8a).
 */
import api from "@/lib/api";
import { parseFiscalInfoError } from "@/modules/despacho/despachoFiscalInfoApi";

export const LOAD_HONORARIOS_ERROR = "No pudimos cargar Honorarios / Cobranza.";
export const LOAD_PRESTADOR_ERROR =
  "No pudimos cargar los datos del despacho (prestador). Revise Configuración → Sistema o intente de nuevo.";

export function parseHonorariosError(err, { fallbackMessage = LOAD_HONORARIOS_ERROR } = {}) {
  return parseFiscalInfoError(err, { fallbackMessage });
}

export const COLLECTION_STATUS_LABELS = {
  pending_review: "Pendiente de revisión",
  pending_payment: "Pendiente de pago",
  pending_payment_complement: "Pendiente complemento",
  partial_payment: "Pago parcial",
  payment_reported: "Cliente informó pago",
  proof_received: "Comprobante recibido",
  paid_confirmed: "Pagado confirmado",
  cancelled: "Cancelado",
  grouped_billing: "Facturación agrupada",
  not_related: "No relacionado",
  no_charge: "Sin cobro",
  suspended: "Suspendido",
};

export const BILLING_MODE_LABELS = {
  direct: "Directa",
  grouped: "Agrupada",
  administrative_only: "Administrativa",
  no_charge: "Sin cobro",
  suspended: "Suspendida",
};

export function collectionStatusLabel(status) {
  return COLLECTION_STATUS_LABELS[status] || status || "—";
}

export function createHonorariosApi(httpClient = api) {
  return {
    async getCapabilities() {
      const { data } = await httpClient.get("/despacho/honorarios/capabilities");
      return data;
    },

    async getDashboard() {
      const { data } = await httpClient.get("/despacho/honorarios/dashboard");
      return data;
    },

    async listInvoices(params = {}) {
      const { data } = await httpClient.get("/despacho/honorarios/invoices", { params });
      return data;
    },

    async exportInvoices(params = {}) {
      const response = await httpClient.get("/despacho/honorarios/invoices/export", {
        params,
        responseType: "blob",
      });
      return response;
    },

    async listAgreements(params = {}) {
      const { data } = await httpClient.get("/despacho/honorarios/agreements", { params });
      return data;
    },

    async listClients() {
      const { data } = await httpClient.get("/despacho/honorarios/clients");
      return data.items || [];
    },

    async listHistory(params = {}) {
      const { data } = await httpClient.get("/despacho/honorarios/history", { params });
      return data.items || [];
    },

    async syncPreview(params = {}) {
      const { data } = await httpClient.post("/despacho/honorarios/sync/preview", params);
      return data;
    },

    async syncConfirm(params = {}) {
      const { data } = await httpClient.post("/despacho/honorarios/sync/confirm", {
        confirm: true,
        ...params,
      });
      return data;
    },

    async createAgreement(body) {
      const { data } = await httpClient.post("/despacho/honorarios/agreements", body);
      return data.item;
    },

    async updateAgreement(id, body) {
      const { data } = await httpClient.put(`/despacho/honorarios/agreements/${id}`, body);
      return data.item;
    },

    async patchCollectionStatus(invoiceId, body) {
      const { data } = await httpClient.patch(
        `/despacho/honorarios/invoices/${invoiceId}/collection-status`,
        body,
      );
      return data.item;
    },

    async bulkPatchCollectionStatus(body) {
      const { data } = await httpClient.post("/despacho/honorarios/invoices/bulk-collection-status", body);
      return data;
    },

    async getPeriodBoard(params = {}) {
      const { data } = await httpClient.get("/despacho/honorarios/period-board", { params });
      return data;
    },

    /** Alinea pestañas de Control con la Lista de clientes (reclasificaciones). */
    async syncPeriodBoardFromRoster({ period } = {}) {
      const { data } = await httpClient.post(
        "/despacho/honorarios/period-board/sync-roster-conditions",
        null,
        { params: period ? { period } : undefined },
      );
      return data;
    },

    async generatePeriodBoard(period) {
      const { data } = await httpClient.post("/despacho/honorarios/period-board/generate", { period });
      return data;
    },

    async getPeriodBoardRow(rowId) {
      const { data } = await httpClient.get(`/despacho/honorarios/period-board/${rowId}`);
      return data;
    },

    async patchPeriodBoardRow(rowId, body) {
      const { data } = await httpClient.patch(`/despacho/honorarios/period-board/${rowId}`, body);
      return data;
    },

    async exportPeriodBoard(params = {}) {
      const response = await httpClient.get("/despacho/honorarios/period-board/export", {
        params,
        responseType: "blob",
      });
      return response;
    },

    async generatePeriodBoardMessage(rowId, templateId) {
      const { data } = await httpClient.post(
        `/despacho/honorarios/period-board/${rowId}/generate-message`,
        { template_id: templateId },
      );
      return data;
    },

    async generatePeriodBoardRemindersBulk({ rowIds, templateId } = {}) {
      const { data } = await httpClient.post("/despacho/honorarios/period-board/generate-reminders", {
        row_ids: rowIds || [],
        template_id: templateId || undefined,
      });
      return data;
    },

    /** Correo real a clientes con fila no cobrada (Cobrada sin marcar). */
    async sendPeriodBoardCollectionEmailsBulk({ rowIds, templateId, force, dryRun } = {}) {
      const { data } = await httpClient.post("/despacho/honorarios/period-board/send-collection-emails", {
        row_ids: rowIds || [],
        template_id: templateId || undefined,
        force: Boolean(force),
        dry_run: Boolean(dryRun),
      });
      return data;
    },

    async sendPeriodBoardCollectionEmail(rowId, { force, dryRun, templateId } = {}) {
      const { data } = await httpClient.post(
        `/despacho/honorarios/period-board/${rowId}/send-collection-email`,
        null,
        {
          params: {
            force: force ? true : undefined,
            dry_run: dryRun ? true : undefined,
            template_id: templateId || undefined,
          },
        },
      );
      return data;
    },

    async getCollectionEmailTemplate() {
      const { data } = await httpClient.get("/despacho/honorarios/period-board/collection-email-template");
      return data;
    },

    async saveCollectionEmailTemplate(body = {}) {
      const { data } = await httpClient.put("/despacho/honorarios/period-board/collection-email-template", {
        subject: body.subject,
        body: body.body,
        attach_cfdi_pdf: body.attachCfdiPdf,
        attach_cfdi_xml: body.attachCfdiXml,
      });
      return data;
    },

    async refreshPayments(body = {}) {
      const { data } = await httpClient.post("/despacho/honorarios/sync/payments-refresh", body);
      return data;
    },

    async getInvoiceDetail(invoiceId) {
      const { data } = await httpClient.get(`/despacho/honorarios/invoices/${invoiceId}`);
      return data;
    },

    async listGroupedAllocations(params = {}) {
      const { data } = await httpClient.get("/despacho/honorarios/grouped-allocations", { params });
      return data;
    },

    async createGroupedAllocation(body) {
      const { data } = await httpClient.post("/despacho/honorarios/grouped-allocations", body);
      return data.item;
    },

    async deleteGroupedAllocation(allocationId) {
      const { data } = await httpClient.delete(`/despacho/honorarios/grouped-allocations/${allocationId}`);
      return data;
    },

    async listPaymentProofs(params = {}) {
      const { data } = await httpClient.get("/despacho/honorarios/payment-proofs", { params });
      return data;
    },

    async uploadPaymentProof(formData) {
      const { data } = await httpClient.post("/despacho/honorarios/payment-proofs", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.item;
    },

    async reviewPaymentProof(proofId, body) {
      const { data } = await httpClient.patch(`/despacho/honorarios/payment-proofs/${proofId}/review`, body);
      return data.item;
    },

    async downloadPaymentProof(proofId) {
      const response = await httpClient.get(`/despacho/honorarios/payment-proofs/${proofId}/download`, {
        responseType: "blob",
      });
      return response;
    },

    async createHonorariosTask(body) {
      const { data } = await httpClient.post("/despacho/honorarios/tasks", body);
      return data.item;
    },

    async listHonorariosTaskKinds() {
      const { data } = await httpClient.get("/despacho/honorarios/task-kinds");
      return data.items || [];
    },

    async listMessageTemplates() {
      const { data } = await httpClient.get("/despacho/honorarios/message-templates");
      return data.items || [];
    },

    async generateMessage(invoiceId, templateId) {
      const { data } = await httpClient.post(
        `/despacho/honorarios/invoices/${invoiceId}/generate-message`,
        { template_id: templateId },
      );
      return data.item;
    },

    async getReport(reportKey) {
      const { data } = await httpClient.get("/despacho/honorarios/reports", { params: { report: reportKey } });
      return data;
    },

    async exportReport(reportKey) {
      const response = await httpClient.get("/despacho/honorarios/reports/export", {
        params: { report: reportKey },
        responseType: "blob",
      });
      return response;
    },

    async getInvoiceControl({ year, source = "facturados" }) {
      const { data } = await httpClient.get("/despacho/honorarios/invoice-control", {
        params: { year, source },
      });
      return data;
    },

    async exportInvoiceControl({ year, view = "annual", source = "facturados" }) {
      const response = await httpClient.get("/despacho/honorarios/invoice-control/export", {
        params: { year, view, source },
        responseType: "blob",
      });
      return response;
    },

    async listClientRoster({ includeEnded = false, billingCondition } = {}) {
      const { data } = await httpClient.get("/despacho/honorarios/client-roster", {
        params: {
          include_ended: includeEnded,
          billing_condition: billingCondition || undefined,
        },
      });
      return data;
    },

    async createClientRoster(body) {
      const { data } = await httpClient.post("/despacho/honorarios/client-roster", body);
      return data.item;
    },

    async updateClientRoster(rosterId, body) {
      const { data } = await httpClient.put(`/despacho/honorarios/client-roster/${rosterId}`, body);
      return data.item;
    },

    async deleteClientRoster(rosterId, { operations_end_at, hard_delete = false } = {}) {
      const { data } = await httpClient.delete(`/despacho/honorarios/client-roster/${rosterId}`, {
        params: { operations_end_at: operations_end_at || undefined, hard_delete },
      });
      return data;
    },

    async seedClientRoster() {
      const { data } = await httpClient.post("/despacho/honorarios/client-roster/seed-from-clients");
      return data;
    },

    async seedClientRosterFromIngresos({ issueDateFrom } = {}) {
      const { data } = await httpClient.post("/despacho/honorarios/client-roster/seed-from-ingresos", null, {
        params: { issue_date_from: issueDateFrom || undefined },
      });
      return data;
    },

    async refreshClientRosterFees() {
      const { data } = await httpClient.post("/despacho/honorarios/client-roster/refresh-fees");
      return data;
    },

    /** CFDI emitidos + clientes despacho + precarga honorarios (una sola acción). */
    async syncClientRosterAll({ issueDateFrom, clearCfdiBefore = false } = {}) {
      const { data } = await httpClient.post("/despacho/honorarios/client-roster/sync-all", null, {
        params: {
          issue_date_from: issueDateFrom || undefined,
          clear_cfdi_before: clearCfdiBefore || undefined,
        },
      });
      return data;
    },

    async assignConvenioGroup(body) {
      const { data } = await httpClient.post(
        "/despacho/honorarios/client-roster/assign-convenio-group",
        body,
      );
      return data;
    },

    async exportClientRoster({
      exportScope = "all",
      billingCondition,
      includeEnded = false,
    } = {}) {
      const response = await httpClient.get("/despacho/honorarios/client-roster/export", {
        params: {
          export_scope: exportScope || "all",
          billing_condition: billingCondition || undefined,
          include_ended: includeEnded,
        },
        responseType: "blob",
      });
      return response;
    },

    async listExtraCharges({ fiscalClientId, periodKey, onlyPending = false } = {}) {
      const { data } = await httpClient.get("/despacho/honorarios/extra-charges", {
        params: {
          fiscal_client_id: fiscalClientId || undefined,
          period_key: periodKey || undefined,
          only_pending: onlyPending,
        },
      });
      return data;
    },

    async createExtraCharge(body) {
      const { data } = await httpClient.post("/despacho/honorarios/extra-charges", body);
      return data.item;
    },

    async updateExtraCharge(chargeId, body) {
      const { data } = await httpClient.put(`/despacho/honorarios/extra-charges/${chargeId}`, body);
      return data.item;
    },

    async deleteExtraCharge(chargeId) {
      const { data } = await httpClient.delete(`/despacho/honorarios/extra-charges/${chargeId}`);
      return data;
    },

    async getServiceContractTemplate() {
      const { data } = await httpClient.get("/despacho/honorarios/service-contracts/template");
      return data;
    },

    async saveServiceContractTemplate(body) {
      const { data } = await httpClient.put("/despacho/honorarios/service-contracts/template", body);
      return data;
    },

    async uploadServiceContractPlantilla(file) {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await httpClient.post("/despacho/honorarios/service-contracts/template/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },

    async downloadServiceContractPlantilla() {
      return httpClient.get("/despacho/honorarios/service-contracts/template/download", {
        responseType: "blob",
      });
    },

    async setServiceContractModeloActivo(modeloActivo) {
      const { data } = await httpClient.put("/despacho/honorarios/service-contracts/template/modelo-activo", {
        modelo_activo: modeloActivo,
      });
      return data;
    },

    async getServiceContractPrestador({ refreshFromProfile = false, timeout = 25000 } = {}) {
      const { data } = await httpClient.get("/despacho/honorarios/service-contracts/prestador", {
        params: refreshFromProfile ? { refresh_from_profile: true } : undefined,
        timeout,
      });
      return data;
    },

    async saveServiceContractPrestador(body) {
      const { data } = await httpClient.put("/despacho/honorarios/service-contracts/prestador", body);
      return data;
    },

    async listServiceContracts({ contractFilter } = {}) {
      const { data } = await httpClient.get("/despacho/honorarios/service-contracts", {
        params: contractFilter && contractFilter !== "all"
          ? { contract_filter: contractFilter }
          : {},
      });
      return data;
    },

    async getServiceContract(fiscalClientId) {
      const { data } = await httpClient.get(
        `/despacho/honorarios/service-contracts/${encodeURIComponent(fiscalClientId)}`,
      );
      return data;
    },

    async previewServiceContractText(fiscalClientId, { fechaInicio, ciudad } = {}) {
      const { data } = await httpClient.post(
        `/despacho/honorarios/service-contracts/${encodeURIComponent(fiscalClientId)}/preview-text`,
        {
          fecha_inicio: fechaInicio || undefined,
          ciudad: ciudad || undefined,
        },
      );
      return data;
    },

    async generateServiceContract(fiscalClientId, { fechaInicio, ciudad, allowIncomplete = false } = {}) {
      const response = await httpClient.post(
        `/despacho/honorarios/service-contracts/${encodeURIComponent(fiscalClientId)}/generate`,
        {
          fecha_inicio: fechaInicio || undefined,
          ciudad: ciudad || undefined,
          allow_incomplete: allowIncomplete,
        },
        { responseType: "blob" },
      );
      return response;
    },

    async previewServiceContractPdf(fiscalClientId = "", {
      body,
      annualIncreasePct,
      fechaInicio,
      contractVariant,
      timeout = 60000,
    } = {}) {
      const payload = {};
      if (body != null && String(body).trim()) payload.body = body;
      if (annualIncreasePct != null && annualIncreasePct !== "") {
        const n = Number(annualIncreasePct);
        if (!Number.isNaN(n)) payload.annual_increase_pct = n;
      }
      if (fechaInicio) payload.fecha_inicio = fechaInicio;
      if (contractVariant) payload.contract_variant = contractVariant;
      const response = await httpClient.post(
        "/despacho/honorarios/service-contracts/preview-pdf",
        Object.keys(payload).length ? payload : {},
        {
          params: fiscalClientId ? { fiscal_client_id: fiscalClientId } : {},
          responseType: "blob",
          timeout,
        },
      );
      return response;
    },

    async downloadServiceContractDraft(fiscalClientId) {
      const response = await httpClient.get(
        `/despacho/honorarios/service-contracts/${encodeURIComponent(fiscalClientId)}/download-draft`,
        { responseType: "blob" },
      );
      return response;
    },

    async downloadServiceContractSigned(fiscalClientId, fileId) {
      const params = fileId ? { file_id: fileId } : {};
      const response = await httpClient.get(
        `/despacho/honorarios/service-contracts/${encodeURIComponent(fiscalClientId)}/download-signed`,
        { responseType: "blob", params },
      );
      return response;
    },

    async uploadServiceContractSigned(fiscalClientId, file) {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await httpClient.post(
        `/despacho/honorarios/service-contracts/${encodeURIComponent(fiscalClientId)}/upload-signed`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return data;
    },

    async deleteServiceContractSignedFile(fiscalClientId, fileId) {
      const { data } = await httpClient.delete(
        `/despacho/honorarios/service-contracts/${encodeURIComponent(fiscalClientId)}/signed/${encodeURIComponent(fileId)}`,
      );
      return data;
    },

    async clearServiceContractSigned(fiscalClientId) {
      const { data } = await httpClient.delete(
        `/despacho/honorarios/service-contracts/${encodeURIComponent(fiscalClientId)}/signed`,
      );
      return data;
    },

    async sendServiceContractEmail(fiscalClientId, {
      force = false,
      dryRun = false,
      allowIncomplete = false,
      fechaInicio,
    } = {}) {
      const { data } = await httpClient.post(
        `/despacho/honorarios/service-contracts/${encodeURIComponent(fiscalClientId)}/send-email`,
        {
          force,
          dry_run: dryRun,
          allow_incomplete: allowIncomplete,
          fecha_inicio: fechaInicio || undefined,
        },
      );
      return data;
    },
  };
}

export const honorariosApi = createHonorariosApi();
